// frontend/app/components/lessonPlans/blocks/LessonAssetBlocks.js
//
// Launchable Saras assets pinned to a lesson step: a specific PWO *scene*, a
// specific video *tag*, and the topic quiz.
//
// Names shown here come from the backend's resolved payload, never from
// strings authored into the lesson — that separation is what lets the same
// lesson render correctly after content is re-cut or re-localized.

import React, { useState } from "react";
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { ActionButton, Pill } from "./LessonBlockShell";
import { assetSummaryFor } from "../assetSummary";
import { evictLessonThumbnail, useLessonThumbnail } from "../lessonThumbnail";

const formatStartTime = (resolved) => {
  if (!resolved) return null;
  if (resolved.tag_start_time) return resolved.tag_start_time;
  const seconds = Number(resolved.start_seconds || 0);
  if (!seconds) return null;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
};

/**
 * Shown instead of a launch button when the backend could not resolve the
 * reference. Anti-goal #6: a broken reference must surface, never silently
 * fall back to some other scene.
 */
const UnresolvedNotice = ({ block, colors, t }) => {
  const translate = t || ((value) => value);
  return (
    <View
      style={[
        styles.unresolved,
        { backgroundColor: colors.incorrectBg, borderColor: colors.incorrectBorder },
      ]}
    >
      <MaterialCommunityIcons name="link-off" size={16} color={colors.danger} />
      <Text style={[styles.unresolvedText, { color: colors.textSecondary }]}>
        {translate("This resource could not be found in the current curriculum.")}
        {block.resolution_error ? `\n${block.resolution_error}` : ""}
      </Text>
    </View>
  );
};

/**
 * "What is this Play With Object (PWO)?" / "Summarize this video segment".
 *
 * Quiet on purpose. Launching the scene is what the teacher came to this card
 * to do, and a second solid button beside it would make the card ask a question
 * it does not need to ask. This is a link with the Saras mark on it, because
 * the Saras rail is where the answer arrives.
 */
const SummaryControl = ({ colors, label, onPress, t }) => {
  const translate = t || ((value) => value);
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={translate(label)}
      style={styles.summaryButton}
    >
      <MaterialCommunityIcons
        name="robot-happy-outline"
        size={15}
        color={colors.accentPrimary}
      />
      <Text style={[styles.summaryButtonText, { color: colors.accentPrimary }]}>
        {translate(label)}
      </Text>
    </TouchableOpacity>
  );
};

/**
 * The large visual at the top of a 3D-scene / video card: the catalogue image
 * when there is one, else a big tinted tile carrying the kind's icon. A play
 * glyph sits over either so the card reads as "press to watch / open".
 */
const MediaThumbnail = ({
  thumbnailPath,
  icon,
  accent,
  colors,
  overlayIcon = "play",
  onPress,
  accessibilityLabel,
}) => {
  const url = useLessonThumbnail(thumbnailPath);
  const [failedUrl, setFailedUrl] = useState(null);
  const showImage = Boolean(url) && failedUrl !== url;

  const Root = onPress ? TouchableOpacity : View;
  const rootProps = onPress
    ? { onPress, accessibilityRole: "button", accessibilityLabel, activeOpacity: 0.85 }
    : {};

  return (
    <Root
      testID="asset-thumbnail"
      {...rootProps}
      style={[styles.thumbnail, { backgroundColor: `${accent}1f`, borderColor: colors.border }]}
    >
      {showImage ? (
        <Image
          testID="asset-thumbnail-image"
          source={{ uri: url }}
          style={styles.thumbnailImage}
          resizeMode="cover"
          onError={() => {
            evictLessonThumbnail(thumbnailPath);
            setFailedUrl(url);
          }}
        />
      ) : (
        <View testID="asset-thumbnail-fallback" style={styles.thumbnailFallback}>
          <MaterialCommunityIcons name={icon} size={40} color={accent} />
        </View>
      )}
      <View style={styles.playBadge} pointerEvents="none">
        <MaterialCommunityIcons name={overlayIcon} size={22} color="#ffffff" />
      </View>
    </Root>
  );
};

const AssetCard = ({
  colors,
  icon,
  accent,
  kindLabel,
  title,
  resolvedName,
  meta,
  instructions,
  launched,
  loading,
  onLaunch,
  launchLabel,
  launchIcon = "play",
  thumbnailOverlayIcon = "play",
  thumbnailLabel,
  block,
  // A media card (3D scene, video) leads with a large thumbnail and a bigger
  // title and launch button. `thumbnailPath` may be null: the tile stands in.
  media = false,
  thumbnailPath = null,
  // Routes this block's summary into the Saras rail, where it is drawn as a
  // question and an answer by the rail's own exchange rendering.
  //
  // ITS ABSENCE REMOVES THE CONTROL, and there is deliberately no in-card
  // fallback. An earlier pass had one — the summary opening as plain text
  // inside this card when no rail was wired — and it was the wrong answer to
  // the right worry. The authored text has to be indistinguishable from a real
  // Saras answer, and a second, plainer rendering of the same words is exactly
  // the lookalike that drifts: the day the rail's bubbles change, the copy in
  // here is the one that looks wrong, and nobody is looking at it. One
  // rendering, in one place, or none.
  onSummary,
  t,
}) => {
  const translate = t || ((value) => value);
  const isResolved = block.resolution_status === "resolved";

  // Drawn only where the press reaches the rail. `onSummary` is passed by the
  // renderer only when a route exists, so this is the whole test.
  const summary = onSummary ? assetSummaryFor(block) : null;

  return (
    <View
      style={[
        styles.card,
        {
          backgroundColor: colors.panelBg,
          borderColor: launched ? accent : colors.border,
        },
      ]}
    >
      {media ? (
        <MediaThumbnail
          thumbnailPath={thumbnailPath}
          icon={icon}
          accent={accent}
          colors={colors}
          overlayIcon={thumbnailOverlayIcon}
          onPress={isResolved && !loading ? onLaunch : undefined}
          accessibilityLabel={thumbnailLabel}
        />
      ) : null}

      <View style={styles.header}>
        {media ? null : (
        <View style={[styles.iconBadge, { backgroundColor: `${accent}22` }]}>
          <MaterialCommunityIcons name={icon} size={20} color={accent} />
        </View>
        )}

        <View style={styles.headerText}>
          <View style={styles.kindRow}>
            <Text style={[styles.kind, { color: accent }]}>{kindLabel}</Text>
            {launched ? (
              <MaterialCommunityIcons
                name="check-circle"
                size={14}
                color={colors.success}
                style={{ marginLeft: 6 }}
              />
            ) : null}
          </View>
          <Text
            style={[
              styles.title,
              media ? styles.mediaTitle : null,
              { color: colors.textPrimary },
            ]}
          >
            {title}
          </Text>
          {resolvedName && resolvedName !== title ? (
            <Text style={[styles.resolvedName, { color: colors.textMuted }]}>
              {resolvedName}
            </Text>
          ) : null}
        </View>
      </View>

      {meta.length > 0 ? (
        <View style={styles.metaRow}>
          {meta.map((entry) => (
            <Pill
              key={entry.label}
              colors={colors}
              label={entry.label}
              icon={entry.icon}
              accent={colors.textSecondary}
            />
          ))}
        </View>
      ) : null}

      {instructions ? (
        <Text style={[styles.instructions, { color: colors.textSecondary }]}>
          {instructions}
        </Text>
      ) : null}

      {isResolved ? (
        <View style={styles.actionRow}>
          {loading ? (
            <ActivityIndicator size="small" color={accent} />
          ) : (
            <ActionButton
              colors={colors}
              label={launched ? translate("Open again") : launchLabel}
              icon={launchIcon}
              accent={accent}
              variant={launched ? "outline" : "solid"}
              size={media ? "large" : "regular"}
              onPress={onLaunch}
            />
          )}
        </View>
      ) : (
        <UnresolvedNotice block={block} colors={colors} t={t} />
      )}

      {/* Below the launch, and shown even when the reference is broken. The
          authored words describe the scene, not the lookup that failed to find
          it — a teacher whose 3D model has gone missing mid-term can still tell
          the room what they were about to see. */}
      {summary ? (
        <View style={styles.summaryRow}>
          <SummaryControl
            colors={colors}
            label={summary.label}
            onPress={() => onSummary(summary)}
            t={t}
          />
        </View>
      ) : null}
    </View>
  );
};

export const PwoBlock = ({ block, colors, entry, loading, onLaunch, onSummary, t }) => {
  const translate = t || ((value) => value);
  const resolved = block.resolved_asset || {};
  const meta = [];
  if (resolved.pwo_name) meta.push({ label: resolved.pwo_name, icon: "cube-outline" });
  if (resolved.scene_display_name) {
    meta.push({ label: resolved.scene_display_name, icon: "layers-outline" });
  }

  return (
    <AssetCard
      colors={colors}
      block={block}
      t={t}
      icon="cube-outline"
      media
      thumbnailPath={resolved.thumbnail_url}
      accent="#10b981"
      kindLabel={translate("3D Scene")}
      title={block.title || resolved.scene_display_name || translate("3D Model")}
      resolvedName={resolved.scene_display_name}
      meta={meta}
      instructions={block.instructions}
      launched={Boolean(entry?.launched)}
      loading={loading}
      onLaunch={onLaunch}
      onSummary={onSummary}
      launchLabel={translate("Launch 3D scene")}
      launchIcon="cube-outline"
      thumbnailOverlayIcon="cube-scan"
      thumbnailLabel={translate("Open 3D scene")}
    />
  );
};

export const VideoBlock = ({ block, colors, entry, loading, onLaunch, onSummary, t }) => {
  const translate = t || ((value) => value);
  const resolved = block.resolved_asset || {};
  const startTime = formatStartTime(resolved);

  const meta = [];
  if (resolved.video_name) meta.push({ label: resolved.video_name, icon: "movie-open-outline" });
  if (startTime) meta.push({ label: `${translate("Starts at")} ${startTime}`, icon: "clock-outline" });

  return (
    <AssetCard
      colors={colors}
      block={block}
      t={t}
      icon="play-circle-outline"
      media
      thumbnailPath={resolved.thumbnail_url}
      accent="#3b82f6"
      kindLabel={translate("Video segment")}
      title={block.title || resolved.tag_name || translate("Concept video")}
      resolvedName={resolved.tag_name}
      meta={meta}
      instructions={block.instructions}
      launched={Boolean(entry?.launched)}
      loading={loading}
      onLaunch={onLaunch}
      onSummary={onSummary}
      launchLabel={translate("Play segment")}
      thumbnailLabel={translate("Play video")}
    />
  );
};

/**
 * The topic quiz. `answeredCount` is how many of its questions this student
 * has already answered — the quiz is a container, so its progress lives in
 * the composite-id entries beside it rather than in its own session entry.
 */
export const TopicQuizBlock = ({
  block,
  colors,
  entry,
  loading,
  onLaunch,
  answeredCount = 0,
  t,
}) => {
  const translate = t || ((value) => value);
  const resolved = block.resolved_asset || {};
  const meta = [];
  if (resolved.question_count) {
    meta.push({
      label: `${resolved.question_count} ${translate("questions")}`,
      icon: "help-circle-outline",
    });
  }
  if (answeredCount > 0) {
    meta.push({
      label: `${answeredCount} ${translate("answered")}`,
      icon: "check-circle-outline",
    });
  }

  return (
    <AssetCard
      colors={colors}
      block={block}
      t={t}
      icon="clipboard-check-outline"
      accent="#8b5cf6"
      kindLabel={translate("Topic Quiz")}
      title={block.title || translate("Topic Quiz")}
      resolvedName={null}
      meta={meta}
      instructions={block.instructions}
      // Answering counts as having opened it, so the tick survives a reload:
      // `launched` is only written when the student presses the button, and
      // the answers outlive that flag.
      launched={Boolean(entry?.launched) || answeredCount > 0}
      loading={loading}
      onLaunch={onLaunch}
      launchLabel={translate("Start quiz")}
    />
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
    marginBottom: 12,
  },
  header: { flexDirection: "row", alignItems: "flex-start" },
  iconBadge: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  headerText: { flex: 1 },
  kindRow: { flexDirection: "row", alignItems: "center" },
  kind: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.7,
    textTransform: "uppercase",
  },
  title: { fontSize: 15, fontWeight: "700", marginTop: 2 },
  mediaTitle: { fontSize: 18, lineHeight: 24 },
  thumbnail: {
    width: "100%",
    height: 180,
    borderRadius: 10,
    borderWidth: 1,
    overflow: "hidden",
    marginBottom: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  thumbnailImage: { width: "100%", height: "100%" },
  thumbnailFallback: { alignItems: "center", justifyContent: "center" },
  playBadge: {
    position: "absolute",
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(0,0,0,0.55)",
    alignItems: "center",
    justifyContent: "center",
  },
  resolvedName: { fontSize: 12, marginTop: 2 },
  metaRow: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 10 },
  instructions: { fontSize: 13, lineHeight: 20, marginTop: 10 },
  actionRow: { flexDirection: "row", marginTop: 12 },
  summaryRow: { marginTop: 10 },
  summaryButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingVertical: 4,
  },
  summaryButtonText: { fontSize: 12, fontWeight: "700", marginLeft: 6 },
  unresolved: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderRadius: 10,
    borderWidth: 1,
    padding: 10,
    marginTop: 12,
    gap: 8,
  },
  unresolvedText: { fontSize: 12, lineHeight: 18, flex: 1, marginLeft: 8 },
});
